import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import * as d3 from "d3";

import {
  CHART_CONFIG,
  GraphContainer,
  Tooltip,
  TooltipValue
} from "./carbonEmissionsGraph.styles";

// Types
interface DataPoint {
  label: string;
  value: number;
}

interface Dimensions {
  width: number;
  height: number;
}

interface Scales {
  xScale: d3.ScalePoint<string>;
  yScale: d3.ScaleLinear<number, number>;
}

interface TooltipState {
  show: boolean;
  x: number;
  y: number;
  data: DataPoint | null;
  showOnLeft?: boolean;
}

interface CarbonEmissionsGraphProps {
  chartData: DataPoint[];
}

/**
 * CarbonEmissionsGraph
 *
 * A responsive line graph showing carbon emissions over time with interactive
 * tooltips and hover effects.
 */
const CarbonEmissionsGraph: React.FC<CarbonEmissionsGraphProps> = ({
  chartData
}) => {
  // Refs
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // State
  const [isReady, setIsReady] = useState(false);
  const [scales, setScales] = useState<Scales>();
  const [tooltip, setTooltip] = useState<TooltipState>({
    show: false,
    x: 0,
    y: 0,
    data: null,
    showOnLeft: false
  });
  const [dimensions, setDimensions] = useState<Dimensions | null>(null);

  // Use a fallback if no data is provided
  const graphData = useMemo(() => {
    return chartData && chartData.length > 0 ? chartData : [];
  }, [chartData]);

  // Initialize sizing and setup resize handling
  useEffect(() => {
    if (!svgRef.current) return;

    setIsReady(true);

    const resizeObserver = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      const { margin } = CHART_CONFIG.dimensions;

      setDimensions({
        width: width - margin.left - margin.right,
        height: height - margin.top - margin.bottom
      });
    });

    resizeObserver.observe(svgRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // useEffect to handle tooltip positioning after render
  useEffect(() => {
    if (tooltip.show && tooltipRef.current && svgRef.current && dimensions) {
      const tooltipWidth = tooltipRef.current.offsetWidth;
      const svgRect = svgRef.current?.getBoundingClientRect();
      const graphRight =
        svgRect.left + dimensions.width + CHART_CONFIG.grid.leftPadding;

      // Check if tooltip would extend beyond graph
      const tooltipRight = tooltip.x + tooltipWidth / 2;

      // Update tooltip position if there's not enough space
      if (tooltipRight > graphRight && !tooltip.showOnLeft) {
        setTooltip(prev => ({
          ...prev,
          showOnLeft: true
        }));
      }
    }
  }, [tooltip.show, tooltip.x, tooltip.data, tooltip.showOnLeft, dimensions]);

  // render the graph when dimensions change
  useEffect(() => {
    if (!isReady || !svgRef.current || !dimensions) return;

    // Clear any existing SVG content
    d3.select(svgRef.current).selectAll("*").remove();

    // If we have no data, stop rendering
    if (graphData.length === 0) return;

    // Setup container with margins
    const svg = d3
      .select(svgRef.current)
      .append("g")
      .attr(
        "transform",
        `translate(${CHART_CONFIG.dimensions.margin.left},${CHART_CONFIG.dimensions.margin.top})`
      );

    // Set up scales
    const xScale = d3
      .scalePoint()
      .domain(graphData.map(d => d.label))
      .range([0, dimensions.width])
      .padding(0);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(graphData, d => d.value) || 1])
      .range([dimensions.height, 0])
      .nice();

    // Store scales for tooltip use
    setScales({ xScale, yScale });

    // Add grid lines
    svg
      .selectAll("grid-line")
      .data(d3.range(0.1, 1.1, 0.1))
      .enter()
      .append("line")
      .attr("x1", CHART_CONFIG.grid.leftPadding)
      .attr("x2", dimensions.width)
      .attr("y1", d => yScale(d))
      .attr("y2", d => yScale(d))
      .attr("stroke", "var(--mode-border-neutral-default)")
      .attr("stroke-opacity", CHART_CONFIG.style.opacity);

    // Add horizontal line above x-axis
    svg
      .append("line")
      .attr("x1", 0)
      .attr("x2", dimensions.width)
      .attr("y1", dimensions.height)
      .attr("y2", dimensions.height)
      .attr("stroke", "var(--mode-border-neutral-default)")
      .attr("stroke-opacity", CHART_CONFIG.style.opacity);

    // Add X axis
    svg
      .append("g")
      .attr("transform", `translate(0,${dimensions.height})`)
      .call(d3.axisBottom(xScale).tickSize(0))
      .style("color", "var(--mode-text-neutral-weak)")
      .selectAll("text")
      .style("font-size", CHART_CONFIG.style.fontSize.xAxis)
      .style("font-weight", CHART_CONFIG.style.fontWeight)
      .attr("dy", "16px");

    // Add a container for the hover month label
    svg
      .append("g")
      .attr("class", "hover-month-container")
      .attr("transform", `translate(0,${dimensions.height + 16})`);

    // Add Y axis
    svg
      .append("g")
      .attr("transform", `translate(${CHART_CONFIG.grid.leftPadding},0)`)
      .call(
        d3
          .axisLeft(yScale)
          .ticks(5)
          .tickSize(0)
          .tickFormat(d => {
            if (d === 0) return "";
            return `${Number(d).toFixed(2)}kg CO2 eq`;
          })
      )
      .style("color", "var(--mode-text-neutral-weakest)")
      .selectAll("text")
      .style("font-size", CHART_CONFIG.style.fontSize.yAxis)
      .style("font-weight", CHART_CONFIG.style.fontWeight);

    // Remove the y-axis domain line
    svg.selectAll(".domain").remove();

    // Add hover tracking line
    const verticalLine = svg
      .append("g")
      .attr("class", "vertical-line")
      .style("display", "none");

    verticalLine
      .append("line")
      .attr("stroke", "var(--mode-border-neutral-strong)")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,4");

    // Create line generator
    const line = d3
      .line<DataPoint>()
      .x(d => xScale(d.label)!)
      .y(d => yScale(d.value))
      .curve(d3.curveLinear);

    // Create gradient for fade-in effect
    const secondMonthX = xScale(graphData[2].label);
    const totalWidth = dimensions.width;
    const gradientPercentage = (secondMonthX! / totalWidth) * 100;

    const gradient = svg
      .append("defs")
      .append("linearGradient")
      .attr("id", "data-line-gradient")
      .attr("x1", "0%")
      .attr("x2", `${gradientPercentage}%`);

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "var(--mode-vector-primary-default)")
      .attr("stop-opacity", "0");

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "var(--mode-vector-primary-default)")
      .attr("stop-opacity", "1");

    // Render the line
    svg
      .append("path")
      .datum(graphData)
      .attr("fill", "none")
      .attr("stroke", "url(#data-line-gradient)")
      .attr("stroke-width", CHART_CONFIG.style.lineWidth)
      .attr("d", line);
  }, [dimensions, isReady, graphData]);
  const updateHoverIndicator = (x: number, height: number, month: string) => {
    if (!svgRef.current) return;

    // Update vertical line
    const verticalLine = d3.select(svgRef.current).select(".vertical-line");
    verticalLine
      .style("display", null)
      .select("line")
      .attr("x1", x)
      .attr("x2", x)
      .attr("y1", 0)
      .attr("y2", height);

    // Update month indicator
    const hoverMonthContainer = d3
      .select(svgRef.current)
      .select(".hover-month-container");

    hoverMonthContainer.selectAll("*").remove();

    hoverMonthContainer
      .append("rect")
      .attr("x", x - 20)
      .attr("y", -10)
      .attr("width", 40)
      .attr("height", 20)
      .attr("fill", "var(--mode-bg-neutral-weakest)");

    hoverMonthContainer
      .append("text")
      .attr("x", x)
      .attr("text-anchor", "middle")
      .attr("fill", "var(--mode-text-neutral-weak)")
      .attr("font-size", CHART_CONFIG.style.fontSize.xAxis)
      .attr("font-weight", CHART_CONFIG.style.fontWeight)
      .attr("dy", "3px")
      .text(month);
  };

  const hideHoverIndicator = () => {
    if (!svgRef.current) return;

    d3.select(svgRef.current).select(".vertical-line").style("display", "none");

    d3.select(svgRef.current)
      .select(".hover-month-container")
      .selectAll("*")
      .remove();
  };

  // Event handlers
  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || !dimensions || !scales) return;

      const svgElement = svgRef.current;
      const { left } = svgElement.getBoundingClientRect();
      const mouseX =
        event.clientX -
        left -
        CHART_CONFIG.dimensions.margin.left -
        CHART_CONFIG.grid.leftPadding;

      // Find nearest month
      const monthWidth = dimensions.width / (graphData.length - 1);
      const halfMonthWidth = monthWidth / 2;

      const monthIndex = graphData.findIndex((_, index) => {
        const monthCenter = index * monthWidth;
        const monthStart = monthCenter - halfMonthWidth;
        const monthEnd = monthCenter + halfMonthWidth;
        return mouseX >= monthStart && mouseX < monthEnd;
      });

      if (monthIndex !== -1) {
        const dataPoint = graphData[monthIndex];
        const rect = svgElement.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Determine tooltip position
        const graphRight = dimensions.width + CHART_CONFIG.grid.leftPadding;
        const cursorPositionInGraph = x + CHART_CONFIG.dimensions.margin.left;

        setTooltip({
          show: true,
          x: cursorPositionInGraph,
          y: y + 16,
          data: dataPoint,
          showOnLeft:
            cursorPositionInGraph >
            graphRight - CHART_CONFIG.tooltip.minSpaceToEdge
        });

        // Update hover indicator
        const cursorX = mouseX + CHART_CONFIG.grid.leftPadding;
        updateHoverIndicator(cursorX, dimensions.height, dataPoint.label);
      }
    },
    [dimensions, scales, graphData]
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip(prev => ({ ...prev, show: false }));
    hideHoverIndicator();
  }, []);

  return (
    <GraphContainer>
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      {tooltip.show && tooltip.data && (
        <Tooltip
          ref={tooltipRef}
          showOnLeft={tooltip.showOnLeft}
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`
          }}
        >
          <div>
            <TooltipValue>{tooltip.data.value.toFixed(2)}</TooltipValue> kg CO2
            eq
          </div>
        </Tooltip>
      )}
    </GraphContainer>
  );
};

export default CarbonEmissionsGraph;
